import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useHasAnyRole } from '@/auth/hooks';
import { AppRole } from '@/auth/roles';
import { cn } from '@/lib/utils';
import type { MessageNode, ModerationDetail } from '../types';
import type { ModerationActions } from '../hooks/useModerationActions';
import { formatDateTime, initials, messageDisplayText } from '../lib/display';

// Message row (parity ModerationDetailsMessageCard.kt): TIP/POST/TEXT rendering; reported node red.
function MessageRow({ node }: { node: MessageNode }) {
  return (
    <div
      className={cn(
        'rounded-lg border p-3',
        node.isReported && 'border-destructive bg-destructive/5',
      )}
    >
      <div className="flex items-center gap-2">
        <Avatar className="size-6">
          {node.avatar && <AvatarImage src={node.avatar} alt={node.author} />}
          <AvatarFallback className="text-[10px]">{initials(node.author)}</AvatarFallback>
        </Avatar>
        <span className="text-sm font-medium">{node.author}</span>
        {node.authorUname && <span className="text-xs text-muted-foreground">@{node.authorUname}</span>}
        {node.isReported && <Badge variant="destructive">Reported</Badge>}
        {node.createdAt && (
          <span className="ml-auto text-xs text-muted-foreground">{formatDateTime(node.createdAt)}</span>
        )}
      </div>
      <p className="mt-1 text-sm">{messageDisplayText(node)}</p>
    </div>
  );
}

/**
 * Reported message card (doc 09 §4.5). Flat message list; Remove-Message hard-deletes the reported
 * message (MySQL-backed row or Firebase-only TEXT message) via DELETE /report/{id}/message, then
 * navigates back to the queue — destructive, confirm + required reason, no undo.
 */
export function ReportedMessageCard({
  report,
  actions,
}: {
  report: ModerationDetail;
  actions: ModerationActions;
}) {
  const canModerate = useHasAnyRole([AppRole.Administrator, AppRole.SystemAdmin, AppRole.ContentManager]);
  if (report.kind !== 'MESSAGE') return null;
  const { message } = report;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Reported Message</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {message.messages.length === 0 ? (
          <p className="text-sm text-muted-foreground">No messages available.</p>
        ) : (
          message.messages.map((m, i) => <MessageRow key={`${m.id || 'm'}-${i}`} node={m} />)
        )}

        {canModerate && (
          <Button
            className="w-full"
            variant="destructive"
            disabled={actions.isPending || message.messages.length === 0}
            onClick={() => void actions.removeMessage()}
          >
            Remove Message
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
